import type { BaseComponentProps, SectionStyle } from '../../types/puck';
import { formatPhone, phoneHref } from "@servicepro/utils";
import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import { Phone } from "lucide-react";
import React from "react";
import { cn } from "../../lib/utils";

/**
 * Says the business takes emergency work, and gives one way to act on it.
 *
 * Deliberately NOT "24/7". The stored fact is `Business.emergencyService`,
 * which means the business takes emergencies — not that anyone is awake at
 * 3am. Writing 24/7 here would be a claim nothing backs.
 *
 * It is a normal in-flow section rather than a fixed bar because jobsites are
 * prerendered: a fixed bar gated on a client effect is absent from the HTML a
 * crawler or an answer engine reads, which for a safety-relevant claim is the
 * whole point of having one.
 */

const EMERGENCY_BAND_DEFAULT_MESSAGE = "Emergency service available.",
  /**
   * The number to call, or null when there is nothing to claim.
   *
   * Strict `=== true` on purpose: the column is `boolean | null`, and null means
   * the owner has never answered, which is not the same as yes.
   */
  resolveEmergencyCall = (
    business: Pick<JobsiteBusinessContext, "emergencyService" | "emergencyPhone"> | undefined,
    fallbackPhone: string | undefined,
  ): { phone: string } | null => {
    if (business?.emergencyService !== true) {
      return null;
    }
    const phone = business.emergencyPhone?.trim() || fallbackPhone?.trim();
    if (phone) {
      return { phone };
    }
    return null;
  };
const EmergencyBand: React.FC<EmergencyBandProps> = ({
  id,
  className,
  message,
  emergencyService,
  phone,
  isEditing,
  navFixed,
  variant = "standard",
}) => {
  const call = resolveEmergencyCall({ emergencyPhone: phone, emergencyService }, undefined),
    // Tolerate a ReactNode in `message`: the editors swap the string for an
    // Inline-editable element. Strings keep the exact old semantics (blank falls back to the default).
    shownMessage = typeof message === "string" || message == null
      ? message?.trim() || EMERGENCY_BAND_DEFAULT_MESSAGE
      : message;

  if (!call) {
    if (isEditing !== true) {
      return <></>;
    }
    return (
      <section
        id={id}
        className={cn(
          "jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/40 jobsite:px-6 jobsite:py-4 jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground",
          className,
        )}
      >
        Appears once emergency service is turned on in Business settings.
      </section>
    );
  }

  if (variant === "bold") {
    // Bold: a band in the accent, the claim in display capitals, and the number
    // On a dark button, so it stops a scroll on a page of dark and light bands.
    return (
      <section
        id={id}
        aria-label="Emergency service"
        className={cn("jobsite:bg-accent jobsite:text-accent-foreground", navFixed && "jobsite:pt-16", className)}
      >
        <div className={cn(BOLD_CONTAINER_CLASS, "jobsite:flex jobsite:flex-col jobsite:items-start jobsite:gap-5 jobsite:py-10 jobsite:md:flex-row jobsite:md:items-center jobsite:md:justify-between jobsite:md:py-12")}>
          <p className="jobsite:m-0 jobsite:max-w-2xl jobsite:font-heading jobsite:text-[1.75rem] jobsite:uppercase jobsite:leading-none jobsite:tracking-[-0.01em] jobsite:text-balance jobsite:sm:text-4xl">
            {shownMessage}
          </p>
          <a
            href={phoneHref(call.phone)}
            data-slot="button"
            data-track="call"
            data-track-label="emergency-band"
            className="jobsite:inline-flex jobsite:h-14 jobsite:shrink-0 jobsite:items-center jobsite:gap-2 jobsite:whitespace-nowrap jobsite:bg-primary jobsite:px-7 jobsite:text-base jobsite:text-primary-foreground jobsite:transition-opacity jobsite:hover:opacity-90"
            style={{ borderRadius: "var(--jobsite-radius)" }}
          >
            <Phone className="jobsite:size-5" aria-hidden="true" />
            Call {formatPhone(call.phone)}
          </a>
        </div>
      </section>
    );
  }

  return (
    <section
      id={id}
      aria-label="Emergency service"
      className={cn(
        "jobsite:bg-primary jobsite:text-primary-foreground",
        navFixed && "jobsite:pt-16",
        className,
      )}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:flex jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:gap-3 jobsite:px-4 jobsite:py-4 jobsite:text-center jobsite:sm:flex-row jobsite:sm:gap-6">
        <p className="jobsite:m-0 jobsite:font-heading jobsite:text-base jobsite:font-semibold">
          {shownMessage}
        </p>
        <a
          href={phoneHref(call.phone)}
          data-slot="button"
          data-track="call"
          data-track-label="emergency-band"
          className="jobsite:inline-flex jobsite:items-center jobsite:gap-2 jobsite:bg-primary-foreground jobsite:px-5 jobsite:py-2 jobsite:font-semibold jobsite:text-primary jobsite:transition-opacity jobsite:hover:opacity-90"
          style={{ borderRadius: "var(--jobsite-radius)" }}
        >
          <Phone className="jobsite:h-4 jobsite:w-4" />
          Call {formatPhone(call.phone)}
        </a>
      </div>
    </section>
  );
};

interface EmergencyBandProps extends BaseComponentProps {
  /** Editable. Everything else about this band comes from the business. */
  message?: string;
  emergencyService?: boolean | null;
  phone?: string;
  /** Renders an explanatory placeholder instead of nothing, in the editor only. */
  isEditing?: boolean;
  /** A transparent header is fixed with no spacer, so the first section clears it. */
  navFixed?: boolean;
  /** Bold: a band in the accent with the claim in display capitals, to match a bold page. */
  variant?: SectionStyle;
}

export type { EmergencyBandProps };
export { EMERGENCY_BAND_DEFAULT_MESSAGE, resolveEmergencyCall, EmergencyBand };
